// src/pages/accounting/VofStatementPage.jsx
// ============================================================================
// Reports > VOF statement (admins only).
//
// The hours Architecture BRIO V.O.F. invoices Studio BRIO Pvt Ltd under the
// intercompany agreement, read from public.vof_hours_statement, which does
// all the classifying (Module A, Module B, SBPL own work, excluded). This page
// picks the period, groups the rows (buildStatement), shows the partner rates
// and exports xlsx and CSV. It never writes a time entry.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Plus, ArrowRightLeft, Undo2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { requireSession } from '../../lib/supabaseAuthHelpers';
import { formatSupabaseError, normalizeId } from '../../hooks/useSupabase';
import {
  BrioFrame,
  BrioTableWrapper,
  BrioTable,
  BrioButton,
  BrioSelect,
  BrioInput,
  BrioInfoBanner,
  BrioEmptyState,
  BrioSectionHeader,
} from '../../components/BrioComponents';
import {
  contractQuarters,
  calendarQuarters,
  calendarMonths,
  todayIso,
  formatDay,
  mapStatementRow,
  buildStatement,
  statementToCsv,
  statementFileName,
  EXCLUDED_BUCKETS,
} from '../../utils/vofStatement';
import { exportVofStatementXlsx } from '../../utils/vofStatementXlsx';

const PAGE = 1000; // PostgREST row cap per request
const VIEW_COLUMNS = 'entry_id, date, user_id, user_name, is_vof_partner, project_id, project_code, project_name, contracting_entity, task_id, stage, hours, description, notes, classification, reason, rate, currency, amount, override_id, override_reason, override_by_name, override_at';

const fmtH = (n) => (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
const fmtMoney = (n, cur) => `${cur === 'EUR' ? '€ ' : cur && cur !== 'mixed' ? `${cur} ` : ''}${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const download = (data, fileName, type) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const num = { textAlign: 'right', whiteSpace: 'nowrap' };

export default function VofStatementPage({ currentUser }) {
  const today = todayIso();
  const options = useMemo(() => ({
    contract: contractQuarters(today),
    calendar: calendarQuarters(today),
    month: calendarMonths(today),
  }), [today]);

  const [mode, setMode] = useState('contract');
  // Opens on the latest contract quarter that has ended: the one due for invoicing.
  const [pick, setPick] = useState(() => (options.contract.find(o => o.to < today) || options.contract[0])?.id || '');
  const [custom, setCustom] = useState({ from: options.contract[0]?.from || today, to: today });

  const period = useMemo(() => {
    if (mode === 'custom') return { from: custom.from, to: custom.to };
    const list = options[mode] || [];
    const hit = list.find(o => o.id === pick) || list[0];
    return hit ? { from: hit.from, to: hit.to } : { from: today, to: today };
  }, [mode, pick, custom, options, today]);

  const changeMode = (m) => {
    setMode(m);
    if (m !== 'custom') setPick((options[m] || [])[0]?.id || '');
  };

  // ── Statement rows ────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loadedFor, setLoadedFor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  const periodKey = `${period.from}|${period.to}`;
  const periodValid = !!period.from && !!period.to && period.from <= period.to;

  const load = useCallback(async () => {
    if (!periodValid) return;
    const req = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const all = [];
      for (let offset = 0; ; offset += PAGE) {
        const { data, error: err } = await supabase
          .from('vof_hours_statement')
          .select(VIEW_COLUMNS)
          .eq('is_vof_partner', true)
          .gte('date', period.from)
          .lte('date', period.to)
          .order('date', { ascending: true })
          .order('entry_id', { ascending: true })
          .range(offset, offset + PAGE - 1);
        if (err) throw err;
        all.push(...(data || []));
        if (!data || data.length < PAGE) break;
      }
      // A slower response for an earlier period must not overwrite this one.
      if (req !== requestRef.current) return;
      setRows(all.map(mapStatementRow));
      setLoadedFor(`${period.from}|${period.to}`);
    } catch (e) {
      if (req !== requestRef.current) return;
      setError(formatSupabaseError(e, { action: 'load', entity: 'VOF statement', role: currentUser?.role }));
      setRows([]);
      setLoadedFor(null);
    } finally {
      if (req === requestRef.current) setLoading(false);
    }
  }, [period.from, period.to, periodValid, currentUser?.role]);

  useEffect(() => { load(); }, [load]);

  // Rows are only read back for the period they were fetched for.
  const statement = useMemo(
    () => buildStatement(loadedFor === periodKey ? rows : []),
    [rows, loadedFor, periodKey],
  );
  const cur = statement.currency;

  // ── Partner rates ─────────────────────────────────────────────────────────
  const [partners, setPartners] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateForm, setRateForm] = useState(null);
  const [rateError, setRateError] = useState('');

  const loadRates = useCallback(async () => {
    const [{ data: us, error: e1 }, { data: rs, error: e2 }] = await Promise.all([
      supabase.from('users').select('id, name').eq('is_vof_partner', true).is('deleted_at', null).order('name'),
      supabase.from('vof_partner_rates').select('id, user_id, hourly_rate, currency, valid_from').order('valid_from', { ascending: false }),
    ]);
    if (e1 || e2) {
      setRateError(formatSupabaseError(e1 || e2, { action: 'load', entity: 'partner rates', role: currentUser?.role }));
      return;
    }
    setPartners((us || []).map(u => ({ id: normalizeId(u.id), name: u.name })));
    setRates((rs || []).map(r => ({
      id: normalizeId(r.id), userId: normalizeId(r.user_id), rate: Number(r.hourly_rate), currency: r.currency, validFrom: r.valid_from,
    })));
  }, [currentUser?.role]);

  useEffect(() => { loadRates(); }, [loadRates]);

  const partnerName = (id) => partners.find(p => String(p.id) === String(id))?.name || `User ${id}`;

  const saveRate = async () => {
    setRateError('');
    const rate = Number(rateForm.rate);
    if (!rateForm.userId) return setRateError('Choose a partner.');
    if (!(rate >= 0) || rateForm.rate === '') return setRateError('Enter a rate.');
    if (!rateForm.validFrom) return setRateError('Enter the date the rate applies from.');
    try {
      await requireSession();
      const { error: err } = await supabase.from('vof_partner_rates').insert({
        user_id: normalizeId(rateForm.userId),
        hourly_rate: rate,
        currency: (rateForm.currency || 'EUR').toUpperCase(),
        valid_from: rateForm.validFrom,
      });
      if (err) return setRateError(formatSupabaseError(err, { action: 'save', entity: 'partner rate', role: currentUser?.role }));
      setRateForm(null);
      await loadRates();
      await load();
    } catch (e) {
      setRateError(e.message);
    }
  };

  // ── Move to SBPL ──────────────────────────────────────────────────────────
  // An admin moves Module A or B entries to SBPL with a reason. The override
  // lives in vof_entry_overrides; the time entry is never touched, and the
  // view ignores an override on anything that is not Module A or B.
  const [selected, setSelected] = useState(() => new Set());
  const [moveReason, setMoveReason] = useState('');
  const [pastReasons, setPastReasons] = useState([]);
  const [moveError, setMoveError] = useState('');
  const [moving, setMoving] = useState(false);

  // Selection belongs to the period it was made in.
  useEffect(() => { setSelected(new Set()); setMoveError(''); }, [periodKey]);

  const loadReasons = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('vof_entry_overrides')
      .select('reason, created_at')
      .order('created_at', { ascending: false })
      .limit(500);
    if (err) return;
    const seen = new Set();
    const list = [];
    (data || []).forEach(r => {
      const t = (r.reason || '').trim();
      if (t && !seen.has(t.toLowerCase())) { seen.add(t.toLowerCase()); list.push(t); }
    });
    setPastReasons(list);
    // Autofill: start from the reason used last, unless one is being typed.
    setMoveReason(prev => prev || list[0] || '');
  }, []);

  useEffect(() => { loadReasons(); }, [loadReasons]);

  const detailIds = useMemo(() => statement.detail.map(r => r.entryId), [statement.detail]);
  const selectedHours = useMemo(
    () => statement.detail.filter(r => selected.has(r.entryId)).reduce((n, r) => n + r.hours, 0),
    [statement.detail, selected],
  );
  const allSelected = detailIds.length > 0 && detailIds.every(id => selected.has(id));
  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(detailIds));

  const moveToSbpl = async () => {
    setMoveError('');
    const reason = moveReason.trim();
    const ids = detailIds.filter(id => selected.has(id));
    if (ids.length === 0) return setMoveError('Tick the entries to move first.');
    if (!reason) return setMoveError('Enter a reason. It is the only record of why these hours are not invoiced.');
    setMoving(true);
    try {
      await requireSession();
      const { error: err } = await supabase
        .from('vof_entry_overrides')
        .insert(ids.map(id => ({ entry_id: id, reason })));
      if (err) {
        setMoveError(formatSupabaseError(err, { action: 'move', entity: 'entries to SBPL', role: currentUser?.role }));
        return;
      }
      setSelected(new Set());
      await Promise.all([load(), loadReasons()]);
    } catch (e) {
      setMoveError(e.message);
    } finally {
      setMoving(false);
    }
  };

  const undoMove = async (row) => {
    setMoveError('');
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .from('vof_entry_overrides')
        .update({ revoked_at: new Date().toISOString(), revoked_by: normalizeId(currentUser?.id) })
        .eq('id', row.overrideId)
        .is('revoked_at', null)
        .select('id');
      if (err) return setMoveError(formatSupabaseError(err, { action: 'undo', entity: 'move to SBPL', role: currentUser?.role }));
      // An UPDATE that RLS filters out matches zero rows instead of raising.
      if (!data || data.length === 0) {
        setMoveError('Not undone: this move may already have been undone elsewhere. The statement has been reloaded.');
      }
      await load();
    } catch (e) {
      setMoveError(e.message);
    }
  };

  // ── Exports ───────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const exportCsv = () => {
    // Byte order mark so Excel reads the CSV as UTF 8.
    download(`﻿${statementToCsv(statement, period)}`, statementFileName(period, 'csv'), 'text/csv;charset=utf-8');
  };
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { buffer, fileName } = await exportVofStatementXlsx(statement, period);
      download(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
    } finally {
      setExporting(false);
    }
  };

  const ready = loadedFor === periodKey && !loading;
  const empty = ready && statement.partners.length === 0;

  return (
    <BrioFrame>
      <div style={{ margin: '16px 0 4px', fontSize: 13 }} className="brio-analytics-muted">
        Partner hours Architecture BRIO V.O.F. invoices Studio BRIO Pvt Ltd. Module A: SBPL projects worked from outside India.
        Module B: Atlas development (BRA, Atlas). Days in a travel log entry for India, BRG and other BRA work, and ABVOF projects are left out.
      </div>

      {/* Period picker */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '16px 0' }}>
        <BrioSelect value={mode} onChange={e => changeMode(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="contract">Contract quarter</option>
          <option value="calendar">Calendar quarter</option>
          <option value="month">Month</option>
          <option value="custom">Custom range</option>
        </BrioSelect>
        {mode === 'custom' ? (
          <>
            <BrioInput type="date" value={custom.from} onChange={e => setCustom({ ...custom, from: e.target.value })} style={{ maxWidth: 170 }} />
            <span className="brio-analytics-muted">to</span>
            <BrioInput type="date" value={custom.to} min={custom.from} onChange={e => setCustom({ ...custom, to: e.target.value })} style={{ maxWidth: 170 }} />
          </>
        ) : (
          <BrioSelect value={pick} onChange={e => setPick(e.target.value)} style={{ maxWidth: 300 }}>
            {(options[mode] || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </BrioSelect>
        )}
        <div style={{ flex: 1 }} />
        <BrioButton variant="secondary" onClick={exportCsv} disabled={!ready || empty}>
          <Download size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />CSV
        </BrioButton>
        <BrioButton onClick={exportXlsx} disabled={!ready || empty || exporting}>
          <Download size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />{exporting ? 'Exporting…' : 'Excel'}
        </BrioButton>
      </div>

      {!periodValid && <BrioInfoBanner>The end date is before the start date.</BrioInfoBanner>}
      {error && <BrioInfoBanner>{error}</BrioInfoBanner>}
      {ready && statement.missingRate > 0 && (
        <BrioInfoBanner>
          {statement.missingRate} billable {statement.missingRate === 1 ? 'entry has' : 'entries have'} no rate for its date and {statement.missingRate === 1 ? 'is' : 'are'} counted at 0. Add a rate below that starts on or before the period.
        </BrioInfoBanner>
      )}

      {!ready ? (
        <BrioEmptyState>Loading statement…</BrioEmptyState>
      ) : empty ? (
        <BrioEmptyState>No partner hours between {formatDay(period.from)} and {formatDay(period.to)}.</BrioEmptyState>
      ) : (
        <>
          {/* Summary */}
          <BrioSectionHeader title={`Summary, ${formatDay(period.from)} to ${formatDay(period.to)}`} />
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr>
                  <th>Partner</th>
                  <th style={num}>Module A h</th>
                  <th style={num}>Module A</th>
                  <th style={num}>Module B h</th>
                  <th style={num}>Module B</th>
                  <th style={num}>Total h</th>
                  <th style={num}>Total</th>
                </tr>
              </thead>
              <tbody>
                {statement.partners.map(p => (
                  <tr key={p.userId}>
                    <td style={{ textTransform: 'capitalize' }}>{p.userName}</td>
                    <td style={num}>{fmtH(p.moduleA.hours)}</td>
                    <td style={num}>{fmtMoney(p.moduleA.amount, cur)}</td>
                    <td style={num}>{fmtH(p.moduleB.hours)}</td>
                    <td style={num}>{fmtMoney(p.moduleB.amount, cur)}</td>
                    <td style={num}>{fmtH(p.total.hours)}</td>
                    <td style={num}>{fmtMoney(p.total.amount, cur)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 600 }}>
                  <td>Total</td>
                  <td style={num}>{fmtH(statement.totals.moduleAHours)}</td>
                  <td style={num}>{fmtMoney(statement.totals.moduleAAmount, cur)}</td>
                  <td style={num}>{fmtH(statement.totals.moduleBHours)}</td>
                  <td style={num}>{fmtMoney(statement.totals.moduleBAmount, cur)}</td>
                  <td style={num}>{fmtH(statement.totals.hours)}</td>
                  <td style={num}>{fmtMoney(statement.totals.amount, cur)}</td>
                </tr>
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Excluded per partner */}
          <BrioSectionHeader title="Excluded hours per partner" />
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr>
                  <th>Partner</th>
                  {EXCLUDED_BUCKETS.map(b => <th key={b} style={num}>{b}</th>)}
                </tr>
              </thead>
              <tbody>
                {statement.partners.map(p => (
                  <tr key={p.userId}>
                    <td style={{ textTransform: 'capitalize' }}>{p.userName}</td>
                    {EXCLUDED_BUCKETS.map(b => <td key={b} style={num}>{fmtH(p.excluded[b])}</td>)}
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Module A per project */}
          <BrioSectionHeader title="Module A per project" />
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr><th>Partner</th><th>Project</th><th style={num}>Hours</th><th style={num}>Amount</th></tr>
              </thead>
              <tbody>
                {statement.projectsA.map(p => (
                  <tr key={`${p.userId}|${p.projectId}`}>
                    <td style={{ textTransform: 'capitalize' }}>{p.userName}</td>
                    <td><strong>{p.projectCode}</strong> {p.projectName}</td>
                    <td style={num}>{fmtH(p.hours)}</td>
                    <td style={num}>{fmtMoney(p.amount, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Detail */}
          <BrioSectionHeader title={`Detail, Module A and Module B (${statement.detail.length} entries)`} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 8px' }}>
            <span className="brio-analytics-muted" style={{ fontSize: 12 }}>
              {selected.size > 0 ? `${selected.size} selected, ${fmtH(selectedHours)} h` : 'Tick entries SBPL bills itself to move them out of the invoice.'}
            </span>
            <div style={{ flex: 1 }} />
            <BrioInput
              list="vof-move-reasons"
              placeholder="Reason"
              aria-label="Reason for moving to SBPL"
              value={moveReason}
              onChange={e => setMoveReason(e.target.value)}
              style={{ minWidth: 240, maxWidth: 360 }}
            />
            <datalist id="vof-move-reasons">
              {pastReasons.map(r => <option key={r} value={r} />)}
            </datalist>
            <BrioButton onClick={moveToSbpl} disabled={selected.size === 0 || moving}>
              <ArrowRightLeft size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />{moving ? 'Moving…' : 'Move to SBPL'}
            </BrioButton>
          </div>
          {moveError && <BrioInfoBanner>{moveError}</BrioInfoBanner>}
          <BrioTableWrapper maxHeight={520}>
            <BrioTable stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input type="checkbox" aria-label="Select all entries" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th>Date</th><th>Partner</th><th>Module</th><th>Project</th><th>Stage</th>
                  <th style={num}>Hours</th><th style={num}>Amount</th><th>Description</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {statement.detail.map(r => (
                  <tr key={r.entryId}>
                    <td>
                      <input type="checkbox" aria-label={`Select entry ${r.entryId}`} checked={selected.has(r.entryId)} onChange={() => toggleRow(r.entryId)} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(r.date)}</td>
                    <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{r.userName}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.classification}</td>
                    <td>{r.projectCode}</td>
                    <td>{r.stage}</td>
                    <td style={num}>{fmtH(r.hours)}</td>
                    <td style={num}>{r.amount == null ? 'no rate' : fmtMoney(r.amount, r.currency)}</td>
                    <td>{r.description}</td>
                    <td>{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Excluded rows */}
          <BrioSectionHeader title={`Excluded partner entries (${statement.excludedRows.length})`} />
          <BrioTableWrapper maxHeight={520}>
            <BrioTable stickyHeader>
              <thead>
                <tr>
                  <th>Date</th><th>Partner</th><th>Reason</th><th>Project</th><th>Stage</th>
                  <th style={num}>Hours</th><th>Description</th><th>Notes</th><th />
                </tr>
              </thead>
              <tbody>
                {statement.excludedRows.map(r => (
                  <tr key={r.entryId}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(r.date)}</td>
                    <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{r.userName}</td>
                    <td>
                      {r.bucket}
                      {r.overrideId != null && (
                        <div className="brio-analytics-muted" style={{ fontSize: 11 }}>
                          {r.overrideReason}{r.overrideByName ? `, by ${r.overrideByName}` : ''}{r.overrideAt ? `, ${formatDay(String(r.overrideAt).slice(0, 10))}` : ''}
                        </div>
                      )}
                    </td>
                    <td>{r.projectCode}</td>
                    <td>{r.stage}</td>
                    <td style={num}>{fmtH(r.hours)}</td>
                    <td>{r.description}</td>
                    <td>{r.notes}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.overrideId != null && (
                        <BrioButton variant="secondary" onClick={() => undoMove(r)} title="Put this entry back on the invoice">
                          <Undo2 size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />Undo
                        </BrioButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>
        </>
      )}

      {/* Partner rates */}
      <BrioSectionHeader
        title="Partner rates"
        right={!rateForm && (
          <BrioButton variant="secondary" onClick={() => { setRateError(''); setRateForm({ userId: '', rate: '', currency: 'EUR', validFrom: period.from }); }}>
            <Plus size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />New rate
          </BrioButton>
        )}
      />
      <div className="brio-analytics-muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Each entry takes the partner's latest rate starting on or before its date. A new rate is added as a new row, so earlier statements keep the rate they were billed at.
      </div>
      {rateForm && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <BrioSelect value={rateForm.userId} onChange={e => setRateForm({ ...rateForm, userId: e.target.value })} style={{ maxWidth: 200 }}>
            <option value="">Partner…</option>
            {partners.map(p => <option key={p.id} value={String(p.id)} style={{ textTransform: 'capitalize' }}>{p.name}</option>)}
          </BrioSelect>
          <BrioInput type="number" min="0" step="0.01" placeholder="Rate per hour" value={rateForm.rate} onChange={e => setRateForm({ ...rateForm, rate: e.target.value })} style={{ maxWidth: 140 }} />
          <BrioInput maxLength={3} value={rateForm.currency} onChange={e => setRateForm({ ...rateForm, currency: e.target.value.toUpperCase() })} style={{ maxWidth: 70 }} />
          <span className="brio-analytics-muted">from</span>
          <BrioInput type="date" value={rateForm.validFrom} onChange={e => setRateForm({ ...rateForm, validFrom: e.target.value })} style={{ maxWidth: 170 }} />
          <BrioButton onClick={saveRate}>Save</BrioButton>
          <BrioButton variant="secondary" onClick={() => setRateForm(null)}>Cancel</BrioButton>
        </div>
      )}
      {rateError && <BrioInfoBanner>{rateError}</BrioInfoBanner>}
      <BrioTableWrapper>
        <BrioTable>
          <thead>
            <tr><th>Partner</th><th style={num}>Rate per hour</th><th>From</th></tr>
          </thead>
          <tbody>
            {rates.map(r => (
              <tr key={r.id}>
                <td style={{ textTransform: 'capitalize' }}>{partnerName(r.userId)}</td>
                <td style={num}>{fmtMoney(r.rate, r.currency)}</td>
                <td>{formatDay(r.validFrom)}</td>
              </tr>
            ))}
            {rates.length === 0 && (
              <tr><td colSpan={3} className="brio-analytics-muted">No rates yet.</td></tr>
            )}
          </tbody>
        </BrioTable>
      </BrioTableWrapper>
    </BrioFrame>
  );
}
